/**
 * @file EmploymentStatusDropdown.js
 * @description A dropdown component for selecting an employment status with filtering capability and tooltip support.
 */

import React, { useState, useEffect } from "react";
import { fetchEmploymentStatuses } from "../../../services/systemService";
import { Dropdown } from "../../helpers/Dropdown";
import LoadingWrapper from "../../ui/loaders/LoadingWrapper";
import Toast from "../../ui/toasts/Toast";
import Tooltip from "../../ui/tooltips/Tooltip";
import { getText } from '../../../i18n';

const EmploymentStatusDropdown = ({
  className,
  inlineLabel = true,
  selectedStatus,
  onStatusChange,
  label = "Employment Status",
  isRequired = false,
  errorMessage,
  hideErrorMessage = false,
  disabled = false,
  showToolTip = false,
  tooltipText = "",
}) => {
  const [statuses, setStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getEmploymentStatuses = async () => {
      setIsLoading(true);
      try {
        const data = await fetchEmploymentStatuses();
        setStatuses(data);
      } catch (error) {
        console.error("Could not fetch employment statuses:", error);
        Toast.error(getText('msg_85e962141af6'));
      } finally {
        setIsLoading(false);
      }
    };

    getEmploymentStatuses();
  }, []);

  const getDefaultLabel = () => {
    const matchingStatus = statuses.find(
      (status) => status.id === selectedStatus
    );
    return matchingStatus ? matchingStatus.name : `Select Employment Status`;
  };

  const filteredStatusOptions = statuses
    .map((status) => ({
      value: status.id,
      label: status.name,
    }));

  return (
    <div
      className={`form-group mb-3 ${inlineLabel ? "inline-input" : ""
        } ${className} ${errorMessage ? "has-error" : ""}`}
    >
      <label htmlFor="status-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
        {showToolTip && (
          <Tooltip html={tooltipText} placement="right">
            <i
              className="bi bi-info-circle"
              style={{
                paddingLeft: "5px",
                fontSize: "13px",
                color: "var(--iNGAGED-Primary-Color)",
              }}
            ></i>
          </Tooltip>
        )}
      </label>
      <LoadingWrapper isLoading={isLoading}>
        <Dropdown
          id="status-dropdown"
          defaultValue={getDefaultLabel()}
          options={filteredStatusOptions}
          value={selectedStatus}
          onChange={onStatusChange}
          disabled={statuses.length === 0 || isLoading || disabled}
        />
      </LoadingWrapper>
      {errorMessage && !hideErrorMessage && (
        <div className="invalid-feedback d-block">{errorMessage}</div>
      )}
    </div>
  );
};

export default EmploymentStatusDropdown;
